import { useQueries, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  ActionFunction,
  LoaderFunction,
  redirect,
  useLoaderData,
  useNavigate,
  useParams,
} from "react-router-dom";

import { get } from "api";
import { Icon } from "atoms/icon/Icon";
import Affirm from "atoms/icon/icons/affirm.svg";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import Klarna from "atoms/icon/icons/klarna.svg";
import RepeatMD from "atoms/icon/icons/RepeatCash.svg";
import { RepeatMDFooter } from "atoms/repeatmd-footer/RepeatMDFooter";
import { getGiftCardTransfersByOrderId } from "modules/giftCards/giftCards.utils";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { useFeatureFlag } from "services/Flags";
import { formatCurrency } from "toolbox/Money";
import { waitObj } from "toolbox/Promise";

const load = async (shopDisplayOrderId: string) => {
  const orderHistoryDetails = queryClient.fetchQuery(
    ["/user/orders/{shopDisplayOrderId}", { shopDisplayOrderId }],
    () => get("/user/orders/{shopDisplayOrderId}", { shopDisplayOrderId })
  );

  return waitObj({
    orderHistoryDetails: orderHistoryDetails,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const orderHistoryDetailsLoader: LoaderFunction = async ({
  params: { shopDisplayOrderId },
}) => {
  if (!shopDisplayOrderId) return redirect("..");
  return await load(shopDisplayOrderId);
};

export const orderHistoryDetailsAction: ActionFunction = async () => {
  return true;
};

export const OrderHistoryDetailsPage = () => {
  const navigate = useNavigate();
  const { shopDisplayOrderId } = useParams();
  const { orderHistoryDetails } = useLoaderData() as LoaderData;
  const isPaidWithRepeatCash =
    orderHistoryDetails.orderSummary.estimatedTotal === 0 &&
    !!orderHistoryDetails.orderSummary.repeatCashApplied;
  const isUnitedDerm = useFeatureFlag("unitedDerm");
  const isGiftCardsEnabled = useFeatureFlag("giftcards");

  const memberships = useQueries({
    queries: orderHistoryDetails.membershipLineItems.map((lineItem) => {
      return {
        queryFn: () =>
          get("/memberships/{membershipId}", {
            membershipId: lineItem.membershipId,
          }),
        queryKey: [
          "/memberships/{membershipId}",
          {
            membershipId: lineItem.membershipId,
          },
        ],
      };
    }),
  });

  const packages = useQueries({
    queries: orderHistoryDetails.packageLineItems.map((lineItem) => {
      return {
        queryFn: () =>
          get("/packages/{packageId}", {
            packageId: lineItem.packageId,
          }),
        queryKey: [
          "/packages/{packageId}",
          {
            packageId: lineItem.packageId,
          },
        ],
      };
    }),
  });

  const { data: giftCardTransfers = [] } = useQuery({
    queryKey: getGiftCardTransfersByOrderId.key({
      orderId: shopDisplayOrderId ?? "",
    }),
    queryFn: () =>
      getGiftCardTransfersByOrderId({
        orderId: shopDisplayOrderId ?? "",
      }),
    enabled: Boolean(shopDisplayOrderId && isGiftCardsEnabled),
  });

  // Get all package IDs that are gift cards for filtering
  const giftCardPackageIds = new Set(
    giftCardTransfers.map((transfer) => String(transfer.giftCardItemId))
  );

  const renderGiftCardName = (transfer: {
    senderPlatformUserId: string;
    recipientPlatformUserId: string;
    recipientInformation: string;
    giftCardItemAmountInCents: number;
  }): string => {
    const isSelfPurchase =
      transfer.senderPlatformUserId === transfer.recipientPlatformUserId;

    if (isSelfPurchase) {
      return `Gift Card for You - ${formatCurrency(
        transfer.giftCardItemAmountInCents
      )}`;
    }

    const recipient = transfer.recipientInformation.split(" ")[0];
    if (recipient) {
      return `Gift Card to ${recipient} - ${formatCurrency(
        transfer.giftCardItemAmountInCents
      )}`;
    }
    return "Gift Card";
  };

  const nonFinancedPaymentName =
    orderHistoryDetails.paymentMethod?.nonFinancedPayment?.name;

  return (
    <div className="relative h-full bg-one">
      <TopNav
        startIconSVG={ChevronLeft}
        showHeaderItems={false}
        onStartIconClick={() => navigate(-1)}
      >
        <div className="flex justify-center text-sub2">ORDER DETAILS</div>
      </TopNav>
      <div className="p-5">
        <div className="flex flex-col gap-5 rounded-lg bg-white px-7 py-6 shadow-subtle">
          <div className="flex flex-col gap-2">
            <div className="flex justify-between">
              <div className="text-bold1 capitalize text-primary">
                Order: {orderHistoryDetails.orderNumber}
              </div>
              <div className="text-body1">
                {dayjs(orderHistoryDetails.date).format("L")}
              </div>
            </div>
            {orderHistoryDetails.platformLocationName && (
              <div className="flex justify-between">
                <div className="text-bold1 capitalize text-primary">
                  Purchase location:
                </div>
                <div className="text-body1">
                  {orderHistoryDetails.platformLocationName}
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-between">
            <div className="flex flex-1 justify-start text-bold3">PRODUCT</div>
            <div className="flex flex-1 justify-center text-bold3">QTY</div>
            <div className="flex flex-1 justify-end text-bold3">PRICE</div>
          </div>
          <div className="flex flex-col gap-4 border-y-[1px] border-light-grey py-4">
            {orderHistoryDetails.packageLineItems
              .filter((lineItem) => {
                return !giftCardPackageIds.has(String(lineItem.packageId));
              })
              .map((lineItem) => {
                const packageModel = packages.find(
                  (packageModel) => packageModel.data?.id === lineItem.packageId
                );
                if (!packageModel) return null;
                return (
                  <div
                    key={lineItem.packageId}
                    className="flex justify-between"
                  >
                    <div className="flex flex-1 justify-start text-body2">
                      {lineItem.displayName &&
                      lineItem.displayName != packageModel.data?.name
                        ? `${lineItem.displayName} - ${packageModel.data?.name}`
                        : packageModel.data?.name}
                    </div>
                    <div className="flex flex-1 justify-center text-body2">
                      {lineItem.quantity || 1}
                    </div>
                    <div className="flex flex-1 justify-end text-body2">
                      {formatCurrency(lineItem.cost)}
                    </div>
                  </div>
                );
              })}
            {orderHistoryDetails.membershipLineItems.map((lineItem) => {
              const membership = memberships.find(
                (membership) => membership.data?.id === lineItem.membershipId
              );
              if (!membership) return null;
              return (
                <div
                  key={lineItem.membershipId}
                  className="flex justify-between"
                >
                  <div className="flex flex-1 justify-start text-body2">
                    {lineItem.displayName &&
                    lineItem.displayName != membership.data?.name
                      ? `${lineItem.displayName} - ${membership.data?.name}`
                      : membership.data?.name}
                  </div>
                  <div className="flex flex-1 justify-center text-body2">1</div>
                  <div className="flex flex-1 justify-end text-body2">
                    {formatCurrency(lineItem.cost)}
                  </div>
                </div>
              );
            })}
            {giftCardTransfers.map((transfer) => {
              const giftCardName = renderGiftCardName(transfer);
              return (
                <div key={transfer.id} className="flex justify-between">
                  <div className="flex flex-1 justify-start text-body2">
                    {giftCardName}
                  </div>
                  <div className="flex flex-1 justify-center text-body2">1</div>
                  <div className="flex flex-1 justify-end text-body2">
                    {formatCurrency(transfer.giftCardItemAmountInCents)}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex justify-between">
              <div className="text-body2">Subtotal</div>
              <div className="text-body2">
                {formatCurrency(orderHistoryDetails.orderSummary.subTotal)}
              </div>
            </div>
            {!!orderHistoryDetails.orderSummary.promoCodeDiscount && (
              <div className="flex justify-between">
                <div className="text-body2">
                  Discount {orderHistoryDetails.promoCode}
                </div>
                <div className="text-body2">
                  {formatCurrency(
                    orderHistoryDetails.orderSummary.promoCodeDiscount
                  )}
                </div>
              </div>
            )}
            {!!orderHistoryDetails.orderSummary.membershipDiscount && (
              <div className="flex justify-between">
                <div className="text-body2">Membership discount</div>
                <div className="text-body2">
                  {formatCurrency(
                    orderHistoryDetails.orderSummary.membershipDiscount
                  )}
                </div>
              </div>
            )}
            {orderHistoryDetails.orderSummary.repeatCashApplied != 0 && (
              <div className="flex justify-between">
                <div className="text-body2">RepeatCash balance</div>
                <div className="text-body2">
                  {formatCurrency(
                    orderHistoryDetails.orderSummary.repeatCashApplied
                  )}
                </div>
              </div>
            )}
            {orderHistoryDetails.orderSummary.serviceFee > 0 && (
              <div className="flex justify-between">
                <div className="text-body2">
                  {!isUnitedDerm ? "Convenience fee" : "Credit card fee"}
                </div>
                <div className="text-body2">
                  {formatCurrency(orderHistoryDetails.orderSummary.serviceFee)}
                </div>
              </div>
            )}
            {orderHistoryDetails.orderSummary.salesTax > 0 && (
              <div className="flex justify-between">
                <div className="text-body2">Tax</div>
                <div className="text-body2">
                  {formatCurrency(
                    orderHistoryDetails.orderSummary.salesTax ?? 0
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-between">
              <div className="text-bold2">Order total</div>
              <div className="text-bold2">
                {formatCurrency(
                  orderHistoryDetails.orderSummary.estimatedTotal
                )}
              </div>
            </div>
            {orderHistoryDetails.refundDetails &&
              orderHistoryDetails.refundDetails.refundedOn && (
                <div className="flex justify-between">
                  <div className="text-body2">
                    Refunded on{" "}
                    {dayjs(orderHistoryDetails.refundDetails.refundedOn).format(
                      "MMM D, YYYY"
                    )}
                  </div>
                  <div className="text-body2">
                    (
                    {formatCurrency(
                      isPaidWithRepeatCash
                        ? orderHistoryDetails.orderSummary.repeatCashApplied
                        : orderHistoryDetails.orderSummary.estimatedTotal
                    )}
                    )
                  </div>
                </div>
              )}
          </div>
          {(nonFinancedPaymentName == "affirm" ||
            nonFinancedPaymentName == "klarna") && (
            <div className="flex flex-col gap-2 border-t-[1px] pt-3">
              <div className="pt-3 text-bold2">Payment method</div>
              <div>
                <Icon
                  svg={nonFinancedPaymentName == "affirm" ? Affirm : Klarna}
                />
              </div>
              <div className="-mt-2 text-body3 text-secondary">
                This purchase was financed through{" "}
                {nonFinancedPaymentName == "affirm" ? "Affirm" : "Klarna"}
              </div>
            </div>
          )}
          {isPaidWithRepeatCash && (
            <div className="flex flex-col gap-2 border-t-[1px] pt-3">
              <div className="pt-3 text-bold2">Payment method</div>
              <div className="flex items-center gap-1">
                <Icon svg={RepeatMD} />
                <div className="text-bold2">RepeatCash</div>
              </div>
              <div className="text-body3 text-secondary">
                This purchase was completed using your RepeatCash balance
              </div>
            </div>
          )}
          {orderHistoryDetails.paymentMethod?.financedPayment?.last4 && (
            <div className="flex flex-col gap-2 border-t-[1px] pt-3">
              <div className="pt-3 text-bold2">Payment method</div>
              <div className="text-body2">
                <span className="capitalize">
                  {orderHistoryDetails.paymentMethod?.financedPayment?.cardType}{" "}
                </span>
                <span>
                  ending in{" "}
                  {orderHistoryDetails.paymentMethod?.financedPayment?.last4}
                </span>
              </div>
              <div className="text-body3 text-secondary">
                This charge will appear as RepeatMD, Inc. on your billing
                statement.
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="relative inset-x-0 bottom-0 mx-auto p-4 pb-9 sm:w-[390px]">
        <RepeatMDFooter />
      </div>
    </div>
  );
};
